import { ReactNode } from 'react';
import { css } from '@emotion/react';
import { theme } from '@frontend/theme';
import { Button, Heading, PopoverMenu, PopoverMenuItem, Text, usePopoverMenu } from '@frontend/design-system';

interface SubscriptionOption<T> {
  label: string;
  value: T;
}

type ColorOption = 'default' | 'neutral' | 'primary' | 'error';

interface FeatureSubscriptionChangeCardProps<T> {
  label: string;
  title: string;
  subTitle?: string;
  color: ColorOption;
  options?: SubscriptionOption<T>[];
  renderOption?: (option: SubscriptionOption<T>) => ReactNode;
  onChange?: (option: SubscriptionOption<T>) => void;
  isDisabled?: boolean;
}

export function FeatureSubscriptionChangeCard<T = string>({
  label,
  title,
  isDisabled,
  onChange,
  renderOption,
  subTitle,
  options = [],
  color = 'default',
}: FeatureSubscriptionChangeCardProps<T>) {
  const {
    getMenuProps,
    getItemProps,
    getTriggerProps,
    open: openMenu,
  } = usePopoverMenu({
    placement: 'bottom-end',
  });

  const isSelectionEnabled = !!options.length;
  return (
    <div css={styles.card(color)}>
      <div css={styles.cardContent}>
        <Text as='span' size='large' css={styles.text(color)}>
          {label}
        </Text>
        <div>
          <Heading level={3} css={styles.title({ color, isClickable: isSelectionEnabled })} onClick={openMenu}>
            {title}
          </Heading>
          <Text as='span' size='large' weight='bold' css={styles.text(color)}>
            {subTitle}
          </Text>
        </div>
      </div>
      {isSelectionEnabled && (
        <Button
          iconName='caret-down-small'
          variant='tertiary'
          destructive={color === 'error'}
          disabled={isDisabled}
          css={{ alignSelf: 'center' }}
          {...getTriggerProps()}
        />
      )}
      <PopoverMenu {...getMenuProps()}>
        {options.map((option, index) => (
          <PopoverMenuItem key={index} {...getItemProps({ index, onClick: () => onChange?.(option) })}>
            {renderOption ? renderOption(option) : <Text size='large'>{option.label}</Text>}
          </PopoverMenuItem>
        ))}
      </PopoverMenu>
    </div>
  );
}

const getBGColor = (color: ColorOption) => {
  switch (color) {
    case 'neutral':
      return theme.colors.neutral5;
    case 'primary':
      return theme.colors.primary5;
    case 'error':
      return theme.colors.critical5;
    default:
      return theme.colors.white;
  }
};

const getBorderColor = (color: ColorOption) => {
  switch (color) {
    case 'default':
      return theme.colors.neutral30;
    default:
      return 'transparent';
  }
};

const getTextColor = (color: ColorOption) => {
  switch (color) {
    case 'neutral':
    case 'default':
      return theme.colors.neutral50;
    case 'primary':
      return theme.colors.primary50;
    case 'error':
      return theme.colors.critical50;

    default:
      return theme.colors.black;
  }
};

const getTitleTextColor = (color: ColorOption) => {
  switch (color) {
    case 'error':
      return theme.colors.critical90;
    case 'default':
      return theme.colors.primary50;
    default:
      return theme.colors.black;
  }
};

const styles = {
  card: (color: ColorOption) =>
    css({
      display: 'flex',
      padding: theme.spacing(2),
      gap: theme.spacing(1),
      borderRadius: theme.borderRadius.large,
      backgroundColor: getBGColor(color),
      color: getTextColor(color),
      border: `1px solid ${getBorderColor(color)}`,
    }),
  cardContent: css({
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    gap: theme.spacing(1),
  }),
  title: ({ color, isClickable }: { color: ColorOption; isClickable: boolean }) =>
    css({
      color: getTitleTextColor(color),
      cursor: isClickable ? 'pointer' : 'default',
    }),
  text: (color: ColorOption) =>
    css({
      color: getTextColor(color),
    }),
};
